import type { FC, ReactNode } from 'react'
import type { NavItem } from '@/types'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu, Search, X } from 'lucide-react'
import { useGSAP } from '@gsap/react'

import { gsap } from '@/lib/gsap'
import { cn } from '@/lib/utils'
import { useT } from '@/i18n'
import { getNav, socials } from '@/constants/germa'
import { Logo } from '@/components/landing/logo'
import { Button } from '@/components/generals/button'
import { Container } from '@/components/generals/container'
import { lockScroll } from '@/components/generals/smooth-scroll'
import { LanguageSwitcher } from '@/components/generals/language-switcher'
import { SearchDialog } from '@/components/generals/search-dialog'
import { InstallAppButton } from '@/components/generals/install-app-button'

const useActive = () => {
    const router = useRouter()
    return (href: string) => {
        if (href === '/') return router.pathname === '/'
        return router.pathname === href || router.pathname.startsWith(`${href}/`)
    }
}

const NavLink = ({
    item,
    scrolled,
    active
}: {
    item: NavItem
    scrolled: boolean
    active: boolean
}): ReactNode => (
    <Link
        href={item.href}
        className={cn(
            'group relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-300',
            scrolled ? 'text-foreground/75 hover:text-foreground' : 'text-parchment/80 hover:text-parchment',
            active && (scrolled ? 'text-foreground' : 'text-parchment')
        )}
    >
        {item.label}
        <span
            className={cn(
                'absolute inset-x-3.5 bottom-1 h-px origin-left rtl:origin-right bg-gold transition-transform duration-300 group-hover:scale-x-100',
                active ? 'scale-x-100' : 'scale-x-0'
            )}
        />
    </Link>
)

const SearchTrigger = ({ scrolled, label, onClick }: { scrolled: boolean, label: string, onClick: () => void }): ReactNode => (
    <button
        type='button'
        onClick={onClick}
        aria-label={label}
        title={label}
        className={cn(
            'grid size-11 place-items-center rounded-full border transition-colors lg:size-10',
            scrolled
                ? 'border-border/70 text-foreground hover:bg-foreground/5'
                : 'border-parchment/30 text-parchment hover:bg-parchment/10'
        )}
    >
        <Search className='size-[18px]' />
    </button>
)

export const Navbar: FC = () => {
    const router = useRouter()
    const t = useT()
    const nav = getNav(t)
    const isActive = useActive()
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)
    const [searchOpen, setSearchOpen] = useState(false)
    const closeSearch = useCallback(() => setSearchOpen(false), [])
    const headerRef = useRef<HTMLElement>(null)
    const linksRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    // Ctrl/Cmd + K or "/" opens the search from anywhere.
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
            if ((event.key === 'k' && (event.metaKey || event.ctrlKey)) || (event.key === '/' && !typing)) {
                event.preventDefault()
                setSearchOpen(true)
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [])

    useEffect(() => {
        lockScroll(open)
        return () => lockScroll(false)
    }, [open])

    useEffect(() => {
        const close = () => setOpen(false)
        router.events.on('routeChangeStart', close)
        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useGSAP(
        () => {
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            if (reduce) return
            gsap.from(headerRef.current, {
                y: -90,
                opacity: 0,
                duration: 1,
                ease: 'power3.out',
                delay: 0.15
            })
        },
        { scope: headerRef, dependencies: [] }
    )

    useGSAP(
        () => {
            if (!open || !linksRef.current) return
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            if (reduce) return
            gsap.from(linksRef.current.children, {
                y: 36,
                opacity: 0,
                duration: 0.62,
                stagger: 0.07,
                ease: 'power3.out',
                delay: 0.1
            })
        },
        { dependencies: [open] }
    )

    return (
        <>
            <header
                ref={headerRef}
                className={cn(
                    'fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500',
                    scrolled
                        ? 'border-b border-border/70 bg-parchment/92 shadow-[0_10px_30px_-22px_rgba(20,25,45,0.55)] backdrop-blur-md'
                        : 'border-b border-transparent bg-transparent'
                )}
            >
                <Container size='wide' className='flex h-[4.5rem] items-center justify-between md:h-20'>
                    <Link href='/' aria-label={t.common.homeAria}>
                        <Logo tone={scrolled ? 'ink' : 'parchment'} />
                    </Link>

                    <nav className='hidden items-center gap-0.5 lg:flex' aria-label={t.common.mainNav}>
                        {nav.map((item) => (
                            <NavLink
                                key={item.href}
                                item={item}
                                scrolled={scrolled}
                                active={isActive(item.href)}
                            />
                        ))}
                    </nav>

                    <div className='hidden items-center gap-3 lg:flex'>
                        <InstallAppButton icon scrolled={scrolled} />
                        <SearchTrigger scrolled={scrolled} label={t.search.open} onClick={() => setSearchOpen(true)} />
                        <LanguageSwitcher scrolled={scrolled} />
                        <Button
                            href='/kontakt'
                            size='sm'
                            variant={scrolled ? 'primary' : 'outline-light'}
                            withArrow
                            className='hidden xl:inline-flex'
                        >
                            {t.common.contactUs}
                        </Button>
                    </div>

                    <div className='flex items-center gap-2 lg:hidden'>
                        <InstallAppButton icon scrolled={scrolled} />
                        <SearchTrigger scrolled={scrolled} label={t.search.open} onClick={() => setSearchOpen(true)} />
                        <LanguageSwitcher scrolled={scrolled} />
                        <button
                            type='button'
                            onClick={() => setOpen(true)}
                            aria-label={t.common.openMenu}
                            className={cn(
                                'grid size-11 place-items-center rounded-full border transition-colors',
                                scrolled
                                    ? 'border-border/70 text-foreground hover:bg-foreground/5'
                                    : 'border-parchment/30 text-parchment hover:bg-parchment/10'
                            )}
                        >
                            <Menu className='size-5' />
                        </button>
                    </div>
                </Container>
            </header>

            <SearchDialog open={searchOpen} onClose={closeSearch} />

            {open && (
                <div className='fixed inset-0 z-[70] flex flex-col bg-navy-deep text-parchment lg:hidden'>
                    <div className='pointer-events-none absolute inset-0 bg-stars opacity-[0.06]' />

                    <div className='relative flex h-[4.5rem] items-center justify-between px-5'>
                        <Logo tone='parchment' />
                        <button
                            type='button'
                            onClick={() => setOpen(false)}
                            aria-label={t.common.closeMenu}
                            className='grid size-11 place-items-center rounded-full border border-parchment/30 text-parchment transition-colors hover:bg-parchment/10'
                        >
                            <X className='size-5' />
                        </button>
                    </div>

                    <nav
                        ref={linksRef}
                        aria-label={t.common.menu}
                        className='relative flex flex-1 flex-col justify-center gap-2 px-6'
                    >
                        {nav.map((item) => (
                            <Link
                                key={item.href}
                                href={item.href}
                                onClick={() => setOpen(false)}
                                className={cn(
                                    'font-display text-3xl font-medium tracking-tight transition-colors sm:text-4xl',
                                    isActive(item.href) ? 'text-gold-soft' : 'text-parchment/85 hover:text-parchment'
                                )}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    <div className='relative border-t border-parchment/10 px-6 pt-6'>
                        <p className='mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-gold-soft/80'>
                            {t.common.language}
                        </p>
                        <LanguageSwitcher variant='list' onNavigate={() => setOpen(false)} />
                    </div>

                    <div className='relative flex items-center gap-3 px-6 py-6'>
                        {socials.map((social) => (
                            <a
                                key={social.label}
                                href={social.href}
                                target='_blank'
                                rel='noopener noreferrer'
                                aria-label={social.label}
                                className='grid size-11 place-items-center rounded-full border border-parchment/20 text-parchment/80 transition-colors hover:border-gold hover:text-gold-soft'
                            >
                                <social.icon className='size-5' />
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </>
    )
}
